import {type ComponentProps, useEffect} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {
  impactAsync,
  ImpactFeedbackStyle,
  notificationAsync,
  NotificationFeedbackType,
} from 'expo-haptics'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {type FBState} from '#/components/capsules/play/types'
import {
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
  useInkStroke,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {IS_WEB} from '#/env'

type Props = {
  state: FBState
  points: number
  maxPoints: number
  explanation?: string
  solutionText?: string
  onRetry?: () => void
  onShowSolution?: () => void
}

/**
 * The verdict after an answer, and the help that goes with it.
 *
 * There is deliberately **no advance action here**. In a book a question's next
 * step is the next question, and that is simply *below* this one: answering
 * already brings it into view, and the page turns from the page's own footer.
 * A "Continue" between them promised a transition the reader could not see —
 * they had already scrolled there. So this bar reports (verdict, score, why)
 * and offers the two things a learner can still need: another try, or the
 * solution. Everything else belongs to the page, not to the question.
 */
export function FeedbackBar({
  state,
  points,
  maxPoints,
  explanation,
  solutionText,
  onRetry,
  onShowSolution,
}: Props) {
  const {t: l} = useLingui()
  // Follows the book theme inside a FlashBook, dark palette everywhere else.
  const p = useFlashBookPlayPalette()
  const d = useFlashBookPlayDesign()
  const ink = useInkStroke()
  // On ruled paper the verdict is not a card: a card has an edge, and an edge
  // is a box sitting on the lines. It is written on the page — the verdict in
  // marker, the score as a plain fraction, the two actions as words you press.
  const printed = d.block.mode === 'ruled'

  const accent =
    state === 'correct'
      ? p.positive
      : state === 'partial' || state === 'solution'
        ? p.warning
        : p.negative

  const icon: ComponentProps<typeof Feather>['name'] =
    state === 'correct'
      ? 'check-circle'
      : state === 'partial'
        ? 'alert-circle'
        : state === 'solution'
          ? 'eye'
          : 'x-circle'

  const feedbackMsg =
    state === 'correct'
      ? l`Correct! Well done!`
      : state === 'partial'
        ? l`Partial credit — ${points} / ${maxPoints} pts`
        : state === 'solution'
          ? l`Solution shown`
          : onRetry
            ? l`Wrong answer — try again!`
            : l`Wrong answer`

  useEffect(() => {
    if (IS_WEB) return
    if (state === 'correct') {
      notificationAsync(NotificationFeedbackType.Success).catch(() => {})
    } else if (state === 'wrong') {
      notificationAsync(NotificationFeedbackType.Error).catch(() => {})
    } else if (state === 'partial') {
      impactAsync(ImpactFeedbackStyle.Medium).catch(() => {})
    }
  }, [state])

  return (
    <View
      style={[
        ss.bar,
        printed
          ? {backgroundColor: 'transparent', borderWidth: 0, padding: 0}
          : {backgroundColor: accent + '18', borderColor: accent + '55'},
      ]}>
      <View style={ss.top}>
        <View style={ss.msgRow}>
          <Feather name={icon} size={16} color={accent} />
          <Text style={[ss.msgTxt, {color: accent}]}>
            {printed ? (
              <Text style={ink(accent)}>{feedbackMsg}</Text>
            ) : (
              feedbackMsg
            )}
          </Text>
        </View>
        <View
          style={
            printed
              ? undefined
              : [
                  ss.scoreChip,
                  {backgroundColor: accent + '22', borderColor: accent + '55'},
                ]
          }>
          <Text style={[ss.scoreTxt, {color: accent}, printed && ss.scoreInk]}>
            {points} / {maxPoints}
          </Text>
        </View>
      </View>

      {state === 'solution' && solutionText ? (
        <Text style={[ss.sol, {color: p.body}]}>{solutionText}</Text>
      ) : explanation ? (
        <Text style={[ss.exp, {color: p.muted}]}>{explanation}</Text>
      ) : null}

      {(state === 'wrong' || state === 'partial') &&
        (onRetry || onShowSolution) && (
          <View style={ss.actions}>
            {onRetry && (
              <TouchableOpacity
                accessibilityRole="button"
                onPress={onRetry}
                style={[
                  ss.ghost,
                  printed
                    ? [ss.ghostPrinted, {borderBottomColor: p.negative}]
                    : {
                        borderColor: p.negative + '66',
                        backgroundColor: p.negative + '14',
                      },
                ]}>
                <Feather name="refresh-cw" size={12} color={p.negative} />
                <Text
                  style={[
                    ss.ghostTxt,
                    {color: p.negative},
                    printed && ss.ghostTxtPrinted,
                  ]}>
                  <Trans>Try again</Trans>
                </Text>
              </TouchableOpacity>
            )}
            {onShowSolution && (
              <TouchableOpacity
                accessibilityRole="button"
                onPress={onShowSolution}
                style={[
                  ss.ghost,
                  printed
                    ? [ss.ghostPrinted, {borderBottomColor: p.muted}]
                    : {borderColor: p.border},
                ]}>
                <Feather name="eye" size={12} color={p.muted} />
                <Text
                  style={[
                    ss.ghostTxt,
                    {color: p.muted},
                    printed && ss.ghostTxtPrinted,
                  ]}>
                  <Trans>Show solution</Trans>
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}
    </View>
  )
}

const ss = StyleSheet.create({
  bar: {borderRadius: 16, borderWidth: 1, padding: 14, gap: 10},
  top: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  msgRow: {flexDirection: 'row', alignItems: 'center', gap: 7, flex: 1},
  msgTxt: {fontSize: 13, fontWeight: '600', flex: 1},
  scoreChip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
    borderWidth: 1,
  },
  scoreTxt: {fontSize: 12, fontWeight: '600'},
  actions: {flexDirection: 'row', gap: 8, flexWrap: 'wrap'},
  ghost: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  ghostTxt: {fontSize: 12, fontWeight: '500'},
  /**
   * On paper the chip's padding is what makes it a chip: the fraction is just
   * ink at the end of the line, the way a teacher writes "3/5".
   */
  scoreInk: {fontWeight: '700', paddingHorizontal: 0},
  /**
   * An action written on the page: no pill, no fill — an underlined word. The
   * underline is the ink the press would put down, so it still reads as the
   * thing you tap without becoming a button on a printed page.
   */
  ghostPrinted: {
    borderWidth: 0,
    borderBottomWidth: 1,
    borderRadius: 0,
    paddingHorizontal: 0,
    paddingVertical: 2,
  },
  ghostTxtPrinted: {fontWeight: '600'},
  sol: {color: 'rgba(255,255,255,0.8)', fontSize: 13, lineHeight: 20},
  exp: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 12,
    lineHeight: 18,
    fontStyle: 'italic',
  },
})
